import React, { useEffect, useRef, useState } from 'react';
import { API_ROOT, sendRequest } from '../../supplementary_components/requests';
import { addNotification, addPopup } from '../../supplementary_components/store';
import { ConfirmationDialogue } from '../../general_components/forms/ConfirmationDialogue';
import { LoadingIcon } from '../../supplementary_components/loading_icon';
import { can, isCompanyAdmin } from '../../supplementary_components/userDataDelegate';
import { prefetchOrgRoles } from './employeeAddConf';
import styles from '../../general_components/content_management/list_view/listView.module.scss';
import roleStyles from './roles.module.scss';
import { Wrapper, Header, Content, Footer, SumbitForm } from '../../general_components/forms/Form';
import { InputFieldDelegate, InputField, SelectField } from '../../general_components/forms/input_fields/inputFieldDelegate';
import { ListViewHeader } from '../../general_components/content_management/list_view/listViewHeader';
import { ListViewFooter } from '../../general_components/content_management/list_view/listViewFooter';
import { ThreeDotsButton } from '../../general_components/content_management/reusableItems/buttons';

const ROLE_FORM_ID = 'roleEditorForm';
const ROLE_COLUMNS = '1.6fr 0.8fr 3rem';
const ROLE_LIST_CONF = {
    columns: [
        { label: 'Role' },
        { label: 'Employees', alignment: 'center' },
        { label: '' },
    ],
};

const emptyPermissions = (catalog) => {
    const next = {};
    (catalog || []).forEach((section) => {
        (section.permissions || []).forEach((item) => {
            next[item.key] = item.kind === 'FLAG' ? false : 'NONE';
        });
    });
    return next;
};

const mergePermissions = (catalog, stored) => {
    const next = emptyPermissions(catalog);
    Object.entries(stored || {}).forEach(([key, value]) => {
        next[key] = value;
    });
    return next;
};

const toSparse = (permissions) => {
    const sparse = {};
    Object.entries(permissions || {}).forEach(([key, value]) => {
        if (value === true || value === 'true' || value === 'MANAGE' || value === 'VIEW') {
            sparse[key] = value === true || value === 'true' ? true : value;
        }
    });
    return sparse;
};

const RoleEditor = ({ closePopup, popupData, postSuccessAction }) => {
    const role = popupData?.role;
    const catalog = popupData?.catalog || [];
    const [permissions, setPermissions] = useState(mergePermissions(catalog, role?.permissions));
    const [saving, setSaving] = useState(false);
    const [loadingHeight, setLoadingHeight] = useState('0');
    const pageRef = useRef(null);
    const canGrantAdmin = isCompanyAdmin();
    const canSave = can('ROLES', 'MANAGE');

    useEffect(() => {
        if (pageRef.current) {
            setLoadingHeight(`${pageRef.current.clientHeight}px`);
        }
    }, []);

    const setValue = (key, value) => {
        setPermissions((prev) => ({ ...prev, [key]: value }));
    };

    const submit = (e) => {
        e.preventDefault();
        if (saving || !canSave) return;
        const name = (e.target.roleName?.value || '').trim();
        if (!name) {
            addNotification({ message: 'Name is required', type: 'alert' });
            return;
        }
        setSaving(true);
        const body = { name, permissions: toSparse(permissions) };
        const method = role?.id ? 'PATCH' : 'POST';
        const link = role?.id ? `${API_ROOT}roles/${role.id}` : `${API_ROOT}roles`;
        sendRequest(method, link, null, (saved) => {
            setSaving(false);
            addNotification({ message: role?.id ? 'Role updated' : 'Role created', type: 'notify', duration: '1200' });
            prefetchOrgRoles();
            postSuccessAction?.(saved);
            closePopup();
        }, (err) => {
            setSaving(false);
            addNotification({ message: err?.message || 'Could not save role', type: 'alert' });
        }, body);
    };

    return (
        <Wrapper ref={pageRef}>
            <LoadingIcon state={saving} height={loadingHeight} />
            <Header><span>{role?.id ? 'Edit Role' : 'Add Role'}</span></Header>
            <Content className="formContent">
                <SumbitForm method="POST" autoComplete="off" id={ROLE_FORM_ID} onSubmit={submit}>
                    <InputFieldDelegate
                        index={0}
                        column={{
                            label: 'Name',
                            name: 'roleName',
                            mandatory: true,
                            autoComplete: 'off',
                            defaultValue: () => role?.name || '',
                        }}
                    />
                    {(catalog || []).map((section) => (
                        <div key={section.key} className={roleStyles.section}>
                            <div className={roleStyles.sectionTitle}>{section.label}</div>
                            {(section.permissions || []).map((item) => {
                                const lockedAdmin = item.key === 'COMPANY_ADMIN' && !canGrantAdmin;
                                const isFlag = item.kind === 'FLAG';
                                const current = permissions[item.key];
                                const selectValue = isFlag
                                    ? (current === true || current === 'true' ? 'true' : 'false')
                                    : (current || 'NONE');
                                return (
                                    <div
                                        key={item.key}
                                        className={`${roleStyles.levelRow} ${lockedAdmin ? roleStyles.locked : ''}`}
                                    >
                                        <span className={roleStyles.permLabel}>{item.label}</span>
                                        <div className={roleStyles.levelSelectWrap}>
                                            <InputField $cusomDisplayMode="block">
                                                <SelectField
                                                    value={selectValue}
                                                    disabled={lockedAdmin}
                                                    onChange={(e) => setValue(
                                                        item.key,
                                                        isFlag ? e.target.value === 'true' : e.target.value,
                                                    )}
                                                >
                                                    {isFlag ? (
                                                        <>
                                                            <option value="false">False</option>
                                                            <option value="true">True</option>
                                                        </>
                                                    ) : (
                                                        <>
                                                            <option value="NONE">None</option>
                                                            <option value="VIEW">View</option>
                                                            <option value="MANAGE">Manage</option>
                                                        </>
                                                    )}
                                                </SelectField>
                                            </InputField>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    ))}
                </SumbitForm>
            </Content>
            <Footer>
                <div className="non-selectable" onClick={closePopup}>Cancel</div>
                {canSave ? (
                    <div className={saving ? 'disabled' : ''}>
                        <input type="submit" form={ROLE_FORM_ID} value={saving ? 'Saving…' : 'Save'} disabled={saving} />
                    </div>
                ) : null}
            </Footer>
        </Wrapper>
    );
};

export const RolesManagement = () => {
    const [roles, setRoles] = useState(null);
    const [catalog, setCatalog] = useState([]);
    const [error, setError] = useState(null);
    const canEdit = can('ROLES', 'MANAGE');

    const load = () => {
        setError(null);
        sendRequest('GET', API_ROOT + 'roles', null, (data) => {
            setRoles(Array.isArray(data) ? data : []);
            prefetchOrgRoles();
        }, (err) => setError(err?.message || 'Failed to load roles'));
        sendRequest('GET', API_ROOT + 'roles/catalog', null, (data) => {
            setCatalog(Array.isArray(data) ? data : []);
        });
    };

    useEffect(() => { load(); }, []);

    const openEditor = (role) => {
        addPopup({
            content: RoleEditor,
            popupData: { role, catalog },
            postSuccessAction: load,
            EZCloseAllowed: true,
            customMinWidth: '42rem',
        });
    };

    const removeRole = (role) => {
        addPopup({
            content: ConfirmationDialogue,
            popupData: {
                name: 'Confirm',
                formTitle: 'Delete Role',
                submitTitle: 'Delete',
                message: `Delete role “${role.name}”?`,
                action: (postResultAction) => {
                    sendRequest('DELETE', `${API_ROOT}roles/${role.id}`, null, () => {
                        addNotification({ message: 'Role deleted', type: 'notify', duration: '1200' });
                        prefetchOrgRoles();
                        load();
                        postResultAction(true);
                    }, () => {
                        addNotification({ message: 'Could not delete role', type: 'alert' });
                        postResultAction(true);
                    });
                },
                cancelAction: (closePopup) => closePopup(),
            },
            EZCloseAllowed: false,
        });
    };

    if (!roles && !error) return <LoadingIcon state={true} height={'10rem'} />;
    if (error) return <div className="errorMessage">{error}</div>;

    return (
        <div>
            {canEdit && catalog.length ? (
                <div className={roleStyles.toolbar}>
                    <div className={`${styles.addButton} non-selectable`} onClick={() => openEditor(null)}>Add Role +</div>
                </div>
            ) : null}
            {roles.length === 0 ? (
                <div className="errorMessage">No roles were found</div>
            ) : (
                <>
                    <div className={styles.container} style={{ '--list-columns': ROLE_COLUMNS }}>
                        <ListViewHeader LabelConf={ROLE_LIST_CONF} columnProportions={ROLE_COLUMNS} />
                        {roles.map((role) => {
                            const canDelete = canEdit && role.code !== 'COMPANY_ADMIN' && (role.employeeCount ?? 0) === 0;
                            return (
                                <div key={role.id} className={styles.row}>
                                    <div>{role.name}</div>
                                    <div style={{ textAlign: 'center' }}>{role.employeeCount ?? 0}</div>
                                    <div className={roleStyles.actionsCell}>
                                        {canEdit ? (
                                            <ThreeDotsButton
                                                position="relative"
                                                editOnly={!canDelete}
                                                onEdit={() => openEditor(role)}
                                                onRemove={canDelete ? () => removeRole(role) : null}
                                            />
                                        ) : null}
                                    </div>
                                </div>
                            );
                        })}
                    </div>
                    <ListViewFooter resultCount={roles.length} />
                </>
            )}
        </div>
    );
};
